import React, { useState, useEffect } from "react";
import { Col, Row, Form, Nav, Card, Button, Badge } from "react-bootstrap";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";
import { useAuth } from '../utils/AuthContext';
import { getDataOnRealtimeDb, updateDataOnRealtimeDb } from '../utils/firebase';
import { toast } from 'react-toastify';
import { updateProfile, sendEmailVerification } from 'firebase/auth';
import { currencyOptions } from '../utils/Catogory';

export default function Settings() {
  const { currentUser, subscription } = useAuth();
  const [activeTab, setActiveTab] = useState('general');
  const [currencies, setCurrencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [subscriptionDetails, setSubscriptionDetails] = useState(null);

  const [settings, setSettings] = useState({
    id: null,
    general: {
      name: '',
      phoneNumber: '',
      currency: 'USD',
      currencySymbol: '$',
      language: 'en',
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
    },
    notifications: {
      email: {
        enabled: true,
        budgetAlerts: true,
        billReminders: true,
        goalProgress: true,
        weeklyReport: true
      },
      frequency: 'daily'
    },
    display: {
      theme: 'light',
      dateFormat: 'MM/DD/YYYY',
      numberFormat: {
        decimals: 2,
        thousandsSeparator: ',',
        decimalSeparator: '.'
      }
    },
    subscription: {
      status: 'free',
      expiryDate: null,
      startDate: null,
      plan: 'free'
    }
  });

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch currencies from the database
      var currencyData = await getDataOnRealtimeDb(`users/${currentUser.uid}/currencies`);
      
      if (currencyData) {
        // Handle the nested structure of currency data
        const firstKey = Object.keys(currencyData)[0];
        if (firstKey) {
          const currencies = Object.values(currencyData[firstKey]);
          setCurrencies(currencies);
        }
      }

      // Fetch settings
      const settingsData = await getDataOnRealtimeDb(`users/${currentUser.uid}/settings`);
      const subscriptionData = await getDataOnRealtimeDb(`users/${currentUser.uid}/subscription`);

      setSubscriptionDetails(subscriptionData);
      const settingsId = settingsData ? Object.keys(settingsData)[0] : null;

      if (settingsData) {
        // Merge with default settings to ensure all fields exist
        const mergedSettings = {
          id: settingsId,
          general: {
            ...settings.general,
            ...settingsData.general
          },
          notifications: {
            email: {
              ...settings.notifications.email,
              ...(settingsData.notifications?.email || {})
            },
            frequency: settingsData.notifications?.frequency || settings.notifications.frequency
          },
          display: {
            ...settings.display,
            numberFormat: {
              ...settings.display.numberFormat,
              ...(settingsData.display?.numberFormat || {})
            },
            ...(settingsData.display || {})
          },
          subscription: {
            ...settings.subscription,
            ...(settingsData.subscription || {})
          }
        };
        setSettings(mergedSettings);
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [currentUser]);

  const handleSave = async (section, data) => {
    try {
      setLoading(true);
      
      // Create the update data structure
      const updateData = {
        ...settings,
        [section]: data
      };

      // Remove the id field from the update data
      const { id, ...cleanUpdateData } = updateData;

      console.log(cleanUpdateData)
      // Update the settings in Firebase
      await updateDataOnRealtimeDb(`users/${currentUser.uid}/settings`, settings.id , cleanUpdateData);

        // If name is updated, also update the user's display name in Firebase Auth
      if (section === 'general' && data.name && data.name !== currentUser.displayName) {
          await updateProfile(currentUser, {
            displayName: data.name
          });
      }

      toast.success('Settings updated successfully');
      await fetchData();
    } catch (error) {
      console.error('Error updating settings:', error);
      toast.error('Failed to update settings');
    } finally {
      setLoading(false);
    }
  };

  const handleCurrencyChange = async (e) => {
    const selectedCurrency = currencies.find(c => c.value === e.target.value);
    if (selectedCurrency) {
      const updatedSettings = {
        ...settings,
        general: {
          ...settings.general,
          currency: selectedCurrency.value,
          currencySymbol: selectedCurrency.symbol
        }
      };
      setSettings(updatedSettings);
      
      try {
        await updateDataOnRealtimeDb(
          `users/${currentUser.uid}/settings`,
          'general',
          updatedSettings.general
        );
        toast.success('Currency updated successfully');
      } catch (error) {
        console.error('Error updating currency:', error);
        toast.error('Failed to update currency');
      }
    }
  };

  const handleVerifyEmail = async () => {
    try {
      await sendEmailVerification(currentUser);
      toast.success('Verification email sent! Please check your inbox.');
    } catch (error) {
      console.error('Error sending verification email:', error);
      toast.error('Failed to send verification email. Please try again later.');
    }
  };

  const renderGeneralSettings = () => {
    if (loading) {
      return <div>Loading...</div>;
    }

    return (
      <Card className="card-settings">
        <Card.Header>
          <Card.Title>General Settings</Card.Title>
          <Card.Text>Manage your account settings and preferences</Card.Text>
        </Card.Header>
        <Card.Body className="p-0">
          <div className="setting-item">
            <Row className="g-2 align-items-center">
              <Col md="5">
                <h6>Display Name</h6>
                <p>How your name appears to others</p>
              </Col>
              <Col md>
                <Form.Control 
                  type="text"
                  value={settings.general.name}
                  onChange={(e) => setSettings({
                    ...settings,
                    general: { ...settings.general, name: e.target.value }
                  })}
                  placeholder={currentUser?.displayName || 'Enter your name'}
                />
              </Col>
            </Row>
          </div>
          <div className="setting-item">
            <Row className="g-2 align-items-center">
              <Col md="5">
                <h6>Email Address</h6>
                <p>Your primary email address</p>
              </Col>
              <Col md>
                <div className="d-flex align-items-center gap-2">
                <Form.Control 
                  type="email"
                  value={currentUser?.email}
                  disabled
                  className="bg-light"
                />
                  {currentUser?.emailVerified ? (
                    <div className="ms-2 d-flex align-items-center">
                      <Badge
                        bg="success"
                        className="d-flex align-items-center px-3 py-2"
                        style={{ fontSize: '0.9rem' }}
                      >
                        <i className="ri-checkbox-circle-fill me-1"></i>
                        Verified
                      </Badge>
                    </div>
                  ) : (
                    <Button
                      variant="warning"
                      size="sm"
                      onClick={handleVerifyEmail}
                      className="ms-2 d-flex align-items-center px-3"
                      style={{
                        borderRadius: '20px',
                        fontSize: '0.9rem',
                        fontWeight: '500',
                        backgroundColor: '#ff9800',
                        border: 'none',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      <i className="ri-mail-send-line me-1"></i>
                      Verify Email
                    </Button>
                  )}
                </div>
                <div className="mt-2">
                  {currentUser?.emailVerified ? (
                    <div className="text-success d-flex align-items-center" style={{ fontSize: '0.875rem' }}>
                      <i className="ri-shield-check-fill me-1"></i>
                      Your email is verified and secure
                    </div>
                  ) : (
                    <div className="text-warning d-flex align-items-center" style={{ fontSize: '0.875rem' }}>
                      <i className="ri-error-warning-fill me-1"></i>
                      Please verify your email address to ensure account security
                    </div>
                  )}
                </div>
              </Col>
            </Row>
          </div>
          <div className="setting-item">
            <Row className="g-2 align-items-center">
              <Col md="5">
                <h6>Phone Number</h6>
                <p>Your contact number for notifications</p>
              </Col>
              <Col md>
                <Form.Control 
                  type="tel"
                  value={settings.general.phoneNumber}
                  onChange={(e) => {
                    // Only allow numbers and basic phone number characters
                    const value = e.target.value.replace(/[^\d+\-() ]/g, '');
                    setSettings({
                      ...settings,
                      general: { ...settings.general, phoneNumber: value }
                    });
                  }}
                  placeholder="Enter your phone number"
                />
                <small className="text-muted mt-1 d-block">
                  Format: +1 (234) 567-8900
                </small>
              </Col>
            </Row>
          </div>
          <div className="setting-item">
            <Row className="g-2 align-items-center">
              <Col md="5">
                <h6>Preferred Currency</h6>
                <p>Currency for all transactions and reports</p>
              </Col>
              <Col md>
                <Form.Select
                  value={settings.general.currency}
                  onChange={handleCurrencyChange}
                  disabled={loading}
                  className="form-select"
                >
                  {currencies.map((currency) => (
                    <option key={currency.value} value={currency.value}>
                      {currency.label} ({currency.symbol})
                    </option>
                  ))}
                </Form.Select>
                <div className="mt-2">
                  <small className="text-muted">
                    Selected currency: {settings.general.currencySymbol} - {
                      currencies.find(c => c.value === settings.general.currency)?.label || 'Loading...'
                    }
                </small>
                </div>
              </Col>
            </Row>
          </div>
        </Card.Body>
        <Card.Footer>
          <Button 
            variant="primary"
            onClick={() => handleSave('general', settings.general)}
            disabled={loading}
          >
            {loading ? 'Saving...' : 'Save Changes'}
          </Button>
        </Card.Footer>
      </Card>
    );
  };

  const renderNotificationSettings = () => (
    <Card className="card-settings">
      <Card.Header>
        <Card.Title>Notification Settings</Card.Title>
        <Card.Text>Manage how you receive notifications</Card.Text>
      </Card.Header>
      <Card.Body className="p-0">
        <div className="setting-item">
          <Row className="g-2">
            <Col md="5">
              <h6>Email Notifications</h6>
              <p>Choose what you want to be notified about</p>
            </Col>
            <Col md>
              <Form.Check 
                type="switch"
                label="Enable Email Notifications"
                checked={settings.notifications.email.enabled}
                onChange={(e) => setSettings({
                  ...settings,
                  notifications: {
                    ...settings.notifications,
                    email: {
                      ...settings.notifications.email,
                      enabled: e.target.checked
                    }
                  }
                })}
              />
              {settings.notifications.email.enabled && (
                <div className="mt-3">
                  <Form.Check 
                    label="Budget Alerts"
                    checked={settings.notifications.email.budgetAlerts}
                    onChange={(e) => setSettings({
                      ...settings,
                      notifications: {
                        ...settings.notifications,
                        email: {
                          ...settings.notifications.email,
                          budgetAlerts: e.target.checked
                        }
                      }
                    })}
                  />
                  <Form.Check 
                    label="Bill Reminders"
                    checked={settings.notifications.email.billReminders}
                    onChange={(e) => setSettings({
                      ...settings,
                      notifications: {
                        ...settings.notifications,
                        email: {
                          ...settings.notifications.email,
                          billReminders: e.target.checked
                        }
                      }
                    })}
                  />
                  <Form.Check 
                    label="Goal Progress Updates"
                    checked={settings.notifications.email.goalProgress}
                    onChange={(e) => setSettings({
                      ...settings,
                      notifications: {
                        ...settings.notifications,
                        email: {
                          ...settings.notifications.email,
                          goalProgress: e.target.checked
                        }
                      }
                    })}
                  />
                  <Form.Check 
                    label="Weekly Report"
                    checked={settings.notifications.email.weeklyReport}
                    onChange={(e) => setSettings({
                      ...settings,
                      notifications: {
                        ...settings.notifications,
                        email: {
                          ...settings.notifications.email,
                          weeklyReport: e.target.checked
                        }
                      }
                    })}
                  />
                </div>
              )}
            </Col>
          </Row>
        </div>
      </Card.Body>
      <Card.Footer>
        <Button 
          variant="primary"
          onClick={() => handleSave('notifications', settings.notifications)}
        >
          Save Changes
        </Button>
      </Card.Footer>
    </Card>
  );

  const renderSubscriptionSettings = () => {
    if (loading) {
      return <div>Loading...</div>;
    }

    const isPremium = subscription === 'premium';
    const { subscription: sub } = settings;

    return (
      <Card className="card-settings">
        <Card.Header>
          <Card.Title>Subscription Status</Card.Title>
          <Card.Text>Manage your subscription and billing</Card.Text>
        </Card.Header>
        <Card.Body className="p-0">
          <div className="setting-item">
            <Row className="g-2">
              <Col md="5">
                <h6>Current Plan</h6>
                <p>Your active subscription plan</p>
              </Col>
              <Col md>
                <div className={`alert alert-${isPremium ? 'success' : 'info'} mb-3`}>
                  <h6 className="mb-2 d-flex align-items-center">
                    <i className={`ri-${isPremium ? 'vip-crown' : 'user'}-fill me-2`}></i>
                    {isPremium ? 'Premium Plan' : 'Free Plan'}
                    {isPremium && (
                      <Badge bg="success" className="ms-2">Active</Badge>
                    )}
                  </h6>
                  {isPremium ? (
                    <p className="mb-0">You have access to all premium features</p>
                  ) : (
                    <div className="mb-0">
                      <p className="mb-2">Upgrade to Premium to unlock:</p>
                      <ul className="list-unstyled mb-3">
                        <li><i className="ri-check-line text-success me-2"></i>AI Budget Optimization</li>
                        <li><i className="ri-check-line text-success me-2"></i>Advanced Analytics</li>
                        <li><i className="ri-check-line text-success me-2"></i>Custom Categories</li>
                        <li><i className="ri-check-line text-success me-2"></i>Priority Support</li>
                      </ul>
                      <Button 
                        variant="primary" 
                        href="/dashboard/pricing"
                        className="w-100"
                      >
                        Upgrade to Premium
                      </Button>
                    </div>
                  )}
                </div>
              </Col>
            </Row>
          </div>
          {isPremium && subscriptionDetails && (
            <>
              <div className="setting-item">
                <Row className="g-2">
                  <Col md="5">
                    <h6>Billing Information</h6>
                    <p>Your subscription billing details</p>
                  </Col>
                  <Col md>
                    <div className="mb-3">
                      <div className="mb-2">
                        <strong>Subscription Id:</strong>
                        <span className="ms-2">{subscriptionDetails.subscriptionId}</span>
                      </div>
                      <div className="mb-2">
                        <strong>Start Date:</strong>
                        <span className="ms-2">{formatDate(subscriptionDetails.startDate)}</span>
                      </div>
                      <div className="mb-2">
                        <strong>Next Billing Date:</strong>
                        <span className="ms-2">{formatDate(subscriptionDetails.expiryDate)}</span>
                      </div>
                      <div>
                        <strong>Plan:</strong>
                        <span className="ms-2">Premium</span>
                      </div>
                    </div>
                    <Button 
                      variant="outline-danger" 
                      size="sm"
                      onClick={() => {
                        toast.info('Please contact support to cancel your subscription');
                      }}
                    >
                      <i className="ri-close-circle-line me-1"></i>
                      Cancel Subscription
                    </Button>
                  </Col>
                </Row>
              </div>
            </>
          )}
        </Card.Body>
        {isPremium && (
          <Card.Footer>
            <div className="d-flex align-items-center justify-content-between">
              <small className="text-muted">
                Need help with your subscription? 
                <Button 
                  variant="link" 
                  className="p-0 ms-1"
                  onClick={() => {
                    toast.info('Support contact information coming soon');
                  }}
                >
                  Contact Support
                </Button>
              </small>
              <Badge bg="primary" className="px-3 py-2">Premium Member</Badge>
            </div>
          </Card.Footer>
        )}
      </Card>
    );
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <React.Fragment>
      <Header />
      <div className="main p-4 p-lg-5">
       
        <h2 className="main-title"></h2>

        <Nav className="nav-line mb-4">
          <Nav.Link 
            active={activeTab === 'general'}
            onClick={() => setActiveTab('general')}
          >
            General
          </Nav.Link>
          <Nav.Link 
            active={activeTab === 'notifications'}
            onClick={() => setActiveTab('notifications')}
          >
            Notifications
          </Nav.Link>
          <Nav.Link 
            active={activeTab === 'subscription'}
            onClick={() => setActiveTab('subscription')}
          >
            Subscription
          </Nav.Link>
        </Nav>

        {activeTab === 'general' && renderGeneralSettings()}
        {activeTab === 'notifications' && renderNotificationSettings()}
        {activeTab === 'subscription' && renderSubscriptionSettings()}

        <Footer />
      </div>
    </React.Fragment>
  );
}
